import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Pressable from '@atlaskit/primitives/pressable';
import CrossIcon from '@atlaskit/icon/core/cross';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';

import { usePageSpaceKey } from '@confluence/page-context';
import { isPersonalSpace } from '@confluence/space-utils/entry-points/isPersonalSpace.ts';

import { StackedContentTypeIcons } from './StackedContentTypeIcons.tsx';
import { ThirdPartyAuthenticationModal } from './ThirdPartyAuthenticationModal.tsx';
import {
	EXTERNAL_PRODUCT_CONNECTION_ICON_URLS,
	type AuthenticationStatus,
	type ExternalProductConnection,
} from './__types__/RemoteObject.ts';

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		marginTop: token('space.100'),
		overflow: 'hidden',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	mainAction: {
		flexGrow: 1,
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.100'),
		backgroundColor: 'transparent',
	},
	iconsWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	dismissButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: token('space.050'),
		backgroundColor: 'transparent',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed to inherit container hover
			backgroundColor: 'transparent',
		},
	},
});

const i18n = defineMessages({
	addContent: {
		id: 'side-navigation.space-navigation.third-party-auth.connect-your-apps',
		defaultMessage: 'Connect your apps',
		description: 'Label for the pill button that opens the content suggestions modal',
	},
	dismiss: {
		id: 'side-navigation.space-navigation.third-party-auth.dismiss',
		defaultMessage: 'Dismiss related content',
		description: 'Accessible label for the button to dismiss the related content panel',
	},
});

type ThirdPartyAuthenticationProps = {
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	onThirdPartyAuthRefetch: () => void | Promise<void>;
	spaceName?: string;
	iconPath?: string;
};

export const ThirdPartyAuthentication = ({
	unauthenticatedServices,
	onThirdPartyAuthRefetch,
	spaceName,
	iconPath,
}: ThirdPartyAuthenticationProps) => {
	const intl = useIntl();
	const [isVisible, setIsVisible] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const hasFiredRenderedRef = useRef(false);
	const [spaceKey] = usePageSpaceKey();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const eligibleConnectors = useMemo(
		() => Object.keys(unauthenticatedServices),
		[unauthenticatedServices],
	);

	const iconUrls = useMemo(
		() => eligibleConnectors.map((service) => EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[service]),
		[eligibleConnectors],
	);

	const isRendered = !isPersonalSpace(spaceKey) && isVisible;

	useEffect(() => {
		if (isRendered && spaceKey && !hasFiredRenderedRef.current) {
			hasFiredRenderedRef.current = true;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'rendered',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceKey,
						entryPoint: 'spaceSidebar',
						suggestionCount: 0,
						eligibleConnectors,
					},
				},
			}).fire();
		}
	}, [isRendered, spaceKey, eligibleConnectors, createAnalyticsEvent]);

	const handleDismiss = () => {
		if (spaceKey) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'dismissed',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceKey,
						entryPoint: 'spaceSidebar',
						dismissMethod: 'close_button',
					},
				},
			}).fire();
		}
		setIsVisible(false);
	};

	const handleAuthModalClose = () => {
		setIsModalOpen(false);
		void onThirdPartyAuthRefetch();
	};

	if (isPersonalSpace(spaceKey) || !isVisible) {
		return null;
	}

	return (
		<>
			<Box xcss={styles.container} testId="space-navigation-third-party-auth">
				<Flex alignItems="center">
					<Pressable xcss={styles.mainAction} onClick={() => setIsModalOpen(true)}>
						<Box xcss={styles.iconsWrapper}>
							<StackedContentTypeIcons iconUrls={iconUrls} />
						</Box>
						<Text weight="medium">{intl.formatMessage(i18n.addContent)}</Text>
					</Pressable>
					<Pressable
						xcss={styles.dismissButton}
						onClick={handleDismiss}
						aria-label={intl.formatMessage(i18n.dismiss)}
					>
						<CrossIcon label="" />
					</Pressable>
				</Flex>
			</Box>
			<ModalTransition>
				{isModalOpen && (
					<ThirdPartyAuthenticationModal
						unauthenticatedServices={unauthenticatedServices}
						onClose={handleAuthModalClose}
						spaceName={spaceName}
						iconPath={iconPath}
					/>
				)}
			</ModalTransition>
		</>
	);
};
